import React, { useState, useEffect, useCallback } from 'react';
import './ImageViewer.css';

const ImageViewer = ({ images, currentIndex, category, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);

  const handleNext = useCallback(() => {
    if (!isZoomed) {
      setIsLoading(true);
      setActiveIndex((prev) => (prev + 1) % images.length);
    }
  }, [images.length, isZoomed]);

  const handlePrev = useCallback(() => {
    if (!isZoomed) {
      setIsLoading(true);
      setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  }, [images.length, isZoomed]);

  const handleKeyDown = useCallback((e) => {
    if (isZoomed) return;
    
    switch (e.key) {
      case 'ArrowRight':
        handleNext();
        break;
      case 'ArrowLeft':
        handlePrev();
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }
  }, [handleNext, handlePrev, onClose, isZoomed]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [handleKeyDown]);

  // Touch events for mobile swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || isZoomed) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrev();
    }
  };

  const handleWhatsAppInquiry = () => {
    const message = encodeURIComponent(
      `Hello! I'm interested in the ${category.name} (Image ${activeIndex + 1}). Can you provide more information about this product?`
    );
    window.open(`https://wa.me/923427374035?text=${message}`, '_blank');
  };

  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
  };

  return (
    <div className="image-viewer">
      <div className="viewer-overlay" onClick={onClose}>
        <div 
          className="viewer-content"
          onClick={e => e.stopPropagation()}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <button className="close-button" onClick={onClose} aria-label="Close viewer">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>

          <div className={`image-container ${isZoomed ? 'zoomed' : ''}`}>
            {isLoading && <div className="loading-spinner" />}
            <img
              src={`${process.env.PUBLIC_URL}${images[activeIndex]}`}
              alt={`${category.name} ${activeIndex + 1}`}
              onLoad={() => setIsLoading(false)}
              onClick={toggleZoom}
              className={isZoomed ? 'zoomed' : ''}
            />
          </div>

          <div className="viewer-controls">
            <button 
              className="nav-button prev" 
              onClick={handlePrev}
              aria-label="Previous image"
              disabled={isZoomed}
            >
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>
            
            <div className="image-info">
              <h3>{category.name}</h3>
              <p>{`Image ${activeIndex + 1} of ${images.length}`}</p>
              {category.details && (
                <div className="image-details">
                  {Object.entries(category.details).map(([key, value]) => (
                    <div key={key} className="detail-item">
                      <span className="detail-label">{key}:</span>
                      <span className="detail-value">
                        {Array.isArray(value) ? value.join(', ') : value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button 
              className="nav-button next" 
              onClick={handleNext}
              aria-label="Next image"
              disabled={isZoomed}
            >
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </button>
          </div>

          <div className="viewer-thumbnails">
            {images.map((image, index) => (
              <button
                key={index}
                className={`thumbnail ${index === activeIndex ? 'active' : ''}`}
                onClick={() => {
                  setIsLoading(true);
                  setActiveIndex(index);
                  setIsZoomed(false);
                }}
                aria-label={`View image ${index + 1}`}
                disabled={isZoomed}
              >
                <img
                  src={`${process.env.PUBLIC_URL}${image}`}
                  alt={`${category.name} thumbnail ${index + 1}`}
                  loading="lazy"
                />
              </button>
            ))}
          </div>

          <button 
            className="whatsapp-inquiry-btn"
            onClick={handleWhatsAppInquiry}
          >
            <span className="whatsapp-icon">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564c.173.087.289.129.332.202.043.073.043.423-.101.827z"/>
              </svg>
            </span>
            Inquire about this product
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;
